import React from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import DetailsPanel, { DetailsRow } from 'src/common/ui/DetailsPanel';
import ExpandableCell from 'src/features/namespace/components/ExpandableCell';
import ExpandableTextCell from 'src/features/namespace/components/ExpandableTextCell';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface MetaBoxProps {
  cxFamilyId: string;
  cxIds: string[];
  /** When set, renders variant-specific metadata */
  variantCxId?: string;
  /** TRANSCOM anchor. Renders as `-` when empty. */
  anchorId?: string | null;
}

/**
 * "Details" panel for the string detail page. Maps the string's metadata to
 * rows and renders them through the shared {@link DetailsPanel}, which the
 * string group detail page reuses too.
 */
const MetaBox: React.FC<MetaBoxProps> = ({
  cxFamilyId,
  cxIds,
  variantCxId,
  anchorId,
}) => {
  const { t } = useTranslation();
  const isVariant = !!variantCxId;

  const rows: DetailsRow[] = [];

  if (isVariant) {
    rows.push({
      label: t('string-meta-type'),
      value: <Badge color="blue">{t('string-meta-variant')}</Badge>,
      testId: 'meta-type-badge',
    });
  }

  rows.push({
    label: t('string-meta-cx-family'),
    value: cxFamilyId,
    testId: 'meta-cx-family',
  });

  // A variant shows its own CX ID in place of the group's list.
  if (isVariant) {
    rows.push({
      label: t('string-meta-cx-id'),
      value: variantCxId,
      testId: 'meta-cxid',
    });
  } else {
    rows.push({
      label: t('string-meta-cx-ids'),
      value: <ExpandableCell items={cxIds} maxVisible={3} />,
      testId: 'meta-cx-ids',
      rich: true,
    });
  }

  // Always rendered: an empty anchor is valid, so the row shows `-`.
  rows.push({
    label: t('string-meta-anchor'),
    value: (
      <ExpandableTextCell
        text={anchorId?.trim() ? anchorId : '-'}
        maxVisible={20}
      />
    ),
    testId: 'meta-anchor',
    rich: true,
  });

  return <DetailsPanel rows={rows} />;
};

export default MetaBox;
